/**
 * Entry point into the year-versioned rule sets.
 *
 * Everything downstream asks `getTaxLaw(year)` and never hard-codes a rate.
 * Years outside the shipped range clamp to the nearest one and report it, so a
 * booking dated 2023 still produces a result instead of throwing.
 */
import { AT_EARLIEST_YEAR, AT_LATEST_YEAR, AT_TAX_YEARS } from './at/years.js';
/**
 * "Ein abgeschlossenes Jahr darf sich nie rückwirkend ändern" as a runtime
 * guarantee, not just a `Readonly<>` annotation: every shipped rule set is
 * frozen at load, so a stray mutation throws (strict mode) instead of quietly
 * rewriting history. `applyOverrides` structuredClones before touching
 * anything, so per-tenant overrides keep working.
 */
function deepFreeze(value) {
    if (value === null || typeof value !== 'object' || Object.isFrozen(value))
        return;
    Object.freeze(value);
    for (const nested of Object.values(value))
        deepFreeze(nested);
}
deepFreeze(AT_TAX_YEARS);
export * from './types.js';
export * from './suggestTaxCode.js';
export { AT_DEFAULT_CATEGORIES, CAPITALISABLE_CATEGORY_CODES, isCapitalisableCategory, isValueDependentVehicleCategory, SYSTEM_CATEGORY_CODES, VALUE_DEPENDENT_VEHICLE_CATEGORY_CODES, } from './at/categories.js';
export { checkKleinunternehmer, KLEINUNTERNEHMER_WARNING_RATE, } from './at/kleinunternehmer.js';
export { AT_TAX_CODES, describeCodeValidity, isTaxCodeValidOn, KLEINUNTERNEHMER_PURCHASE_CODES, KLEINUNTERNEHMER_SALES_CODES, } from './at/taxCodes.js';
export { UVA_EXEMPT_KZ, UVA_INPUT_VAT_KZ, UVA_SUM_ADD, UVA_SUM_SUBTRACT, } from './at/uva.js';
export { E1A_FALLBACK_EXPENSE_KZ, E1A_FALLBACK_INCOME_KZ } from './at/e1a.js';
export { AT_EARLIEST_YEAR, AT_LATEST_YEAR, AT_TAX_YEARS };
export const SUPPORTED_YEARS = Object.keys(AT_TAX_YEARS)
    .map(Number)
    .sort((a, b) => a - b);
export function clampYear(year) {
    if (year < AT_EARLIEST_YEAR)
        return AT_EARLIEST_YEAR;
    if (year > AT_LATEST_YEAR)
        return AT_LATEST_YEAR;
    return year;
}
export function isSupportedYear(year) {
    return year in AT_TAX_YEARS;
}
/**
 * The rule set in force for `year`. Requesting a year we do not ship yet
 * returns the latest one — a new calendar year keeps working until its own
 * figures are added.
 */
export function getTaxLaw(year) {
    return AT_TAX_YEARS[clampYear(year)];
}
export function getTaxCode(year, key) {
    return getTaxLaw(year).taxCodes.find((code) => code.key === key);
}
/** Throws when a booking references a code that does not exist in its year. */
export function requireTaxCode(year, key) {
    const code = getTaxCode(year, key);
    if (!code)
        throw new Error(`Unknown tax code "${key}" for year ${year}`);
    return code;
}
/** Codes a user may pick on a document line — no `deprecated`, no `systemOnly`. */
export function selectableTaxCodes(codes) {
    return codes.filter((code) => !code.deprecated && !code.systemOnly);
}
export function salesTaxCodes(year) {
    return selectableTaxCodes(getTaxLaw(year).taxCodes).filter((code) => code.side === 'sales');
}
export function purchaseTaxCodes(year) {
    return selectableTaxCodes(getTaxLaw(year).taxCodes).filter((code) => code.side === 'purchase');
}
/** Apply tenant-specific overrides stored as dot paths onto a rule set. */
export function applyOverrides(law, overrides) {
    if (overrides.length === 0)
        return law;
    const clone = structuredClone(law);
    for (const { path, value } of overrides) {
        const segments = path.split('.');
        let cursor = clone;
        for (const segment of segments.slice(0, -1)) {
            const next = cursor[segment];
            if (typeof next !== 'object' || next === null)
                break;
            cursor = next;
        }
        const leaf = segments.at(-1);
        if (leaf)
            cursor[leaf] = value;
    }
    return clone;
}
//# sourceMappingURL=index.js.map